import { Controller } from '@hotwired/stimulus'

// The settings rail: lg+ is the full sticky column (untouched), md is a slim icon-only rail where
// each link gets a Bootstrap tooltip, and below md the rail is replaced by a "current section"
// button that opens `.settings-nav-sheet` (a bottom offcanvas listing every item). This controller
// owns the tooltip lifecycle (scoped to the md band only — at lg the label is already visible, and
// below md the rail itself is hidden) and the sheet's swipe-down-to-dismiss gesture, mirroring
// ordering_layout_controller's pull-down-from-scroll-top pattern (sheetScrollDragStart/Move/End,
// settleSheet).
export default class extends Controller {
  static targets = ['tooltip', 'sheet', 'sheetBody']

  connect() {
    this.mdQuery = window.matchMedia('(min-width: 768px) and (max-width: 991.98px)')
    this.onMdChange = () => this.syncTooltips()
    this.mdQuery.addEventListener('change', this.onMdChange)
    this.syncTooltips()

    // Phones: the tab row scrolls sideways, so bring the current page into view.
    const list = this.element.querySelector('.settings-rail__list')
    const active = list && list.querySelector('.settings-rail__link.active')
    // Rects, not offsetLeft: offsetLeft counts from a positioned ancestor, which put the tab under the left fade.
    if (active && list.scrollWidth > list.clientWidth) {
      const from = active.getBoundingClientRect().left - list.getBoundingClientRect().left
      list.scrollLeft += from - (list.clientWidth - active.offsetWidth) / 2
    }
    if (list) {
      this.list = list
      this.onListScroll = () => this.syncFades()
      list.addEventListener('scroll', this.onListScroll, { passive: true })
      window.addEventListener('resize', this.onListScroll)
      this.syncFades()
    }

    // The whole sheet listens (grabber and header included), not only the scrolling list.
    if (this.hasSheetTarget) {
      this.onSheetTouchStart = this.sheetDragStart.bind(this)
      this.onSheetTouchMove = this.sheetDragMove.bind(this)
      this.onSheetTouchEnd = this.sheetDragEnd.bind(this)
      this.sheetTarget.addEventListener('touchstart', this.onSheetTouchStart, { passive: true })
      this.sheetTarget.addEventListener('touchmove', this.onSheetTouchMove, { passive: false })
      this.sheetTarget.addEventListener('touchend', this.onSheetTouchEnd, { passive: true })
      this.sheetTarget.addEventListener('touchcancel', this.onSheetTouchEnd, { passive: true })
    }
  }

  // Marks which ends of the phone tab row still have tabs past them; the CSS fades only those ends.
  syncFades() {
    const { scrollLeft, scrollWidth, clientWidth } = this.list
    this.list.toggleAttribute('data-fade-start', scrollLeft > 1)
    this.list.toggleAttribute('data-fade-end', scrollLeft + clientWidth < scrollWidth - 1)
  }

  disconnect() {
    if (this.list) {
      this.list.removeEventListener('scroll', this.onListScroll)
      window.removeEventListener('resize', this.onListScroll)
    }
    this.mdQuery.removeEventListener('change', this.onMdChange)
    this.tooltipTargets.forEach((el) => bootstrap.Tooltip.getInstance(el)?.dispose())

    if (this.hasSheetTarget && this.onSheetTouchStart) {
      this.sheetTarget.removeEventListener('touchstart', this.onSheetTouchStart)
      this.sheetTarget.removeEventListener('touchmove', this.onSheetTouchMove)
      this.sheetTarget.removeEventListener('touchend', this.onSheetTouchEnd)
      this.sheetTarget.removeEventListener('touchcancel', this.onSheetTouchEnd)
    }
  }

  // A drag that starts on the grabber or header always pulls the sheet; one inside the list only while it's scrolled to the top.
  listAtTop(event) {
    return !this.hasSheetBodyTarget || !this.sheetBodyTarget.contains(event.target) || this.sheetBodyTarget.scrollTop <= 0
  }

  // Only enabled at md: at lg the label is already visible beside the icon (a tooltip would be
  // redundant chrome on the untouched desktop rail), and below md this rail is hidden entirely.
  syncTooltips() {
    this.tooltipTargets.forEach((el) => {
      const existing = bootstrap.Tooltip.getInstance(el)
      if (this.mdQuery.matches) {
        if (!existing) bootstrap.Tooltip.getOrCreateInstance(el, { placement: 'right' })
      } else {
        existing?.dispose()
      }
    })
  }

  // --- Sheet swipe-down-to-dismiss ---
  // Engages only when the drag starts with the sheet body scrolled to the top and keeps moving
  // down, so normal scrolling inside a long item list is untouched.
  sheetDragStart(event) {
    this.dragAtTop = this.listAtTop(event)
    this.dragStartY = event.touches[0].clientY
    this.dragDelta = 0
    this.dragEngaged = false
  }

  sheetDragMove(event) {
    const delta = event.touches[0].clientY - this.dragStartY

    if (!this.dragEngaged) {
      const atTopNow = this.listAtTop(event)
      if (this.dragAtTop && atTopNow && delta > 0) {
        this.dragEngaged = true
        this.sheetTarget.style.transition = 'none'
      } else {
        return
      }
    }

    // User reversed upward mid-gesture — release back to native scroll.
    if (delta <= 0) {
      this.dragDelta = 0
      this.dragEngaged = false
      this.sheetTarget.style.transition = ''
      this.sheetTarget.style.transform = ''
      return
    }

    event.preventDefault()
    this.dragDelta = delta
    this.sheetTarget.style.transform = `translateY(${delta}px)`
  }

  sheetDragEnd() {
    if (this.dragEngaged) {
      this.settleSheet()
    } else {
      this.sheetTarget.style.transition = ''
      this.sheetTarget.style.transform = ''
    }
    this.dragEngaged = false
    this.dragDelta = 0
  }

  settleSheet() {
    const sheet = this.sheetTarget
    const dismissed = this.dragDelta > Math.min(sheet.offsetHeight * 0.25, 160)

    // Restore the CSS-driven transition so both spring-back and dismiss animate.
    sheet.style.transition = ''

    if (dismissed) {
      // Continue the downward motion (no spring-up), in sync with Bootstrap's own hide.
      sheet.style.transform = 'translateY(100%)'
      bootstrap.Offcanvas.getOrCreateInstance(sheet).hide()
      const cleanup = () => {
        sheet.style.transform = ''
        sheet.removeEventListener('transitionend', cleanup)
      }
      sheet.addEventListener('transitionend', cleanup, { once: true })
      setTimeout(cleanup, 450)
    } else {
      sheet.style.transform = ''
    }
  }
}
